<template>
  <v-dialog 
    v-model="dialog" 
    max-width="900" 
    persistent 
    scrollable
    transition="dialog-bottom-transition"
  >
    <v-card class="oferta-update-card">
      <!-- Header con gradiente -->
      <v-card-title class="oferta-header pa-5">
        <div class="d-flex align-center w-100">
          <div class="header-icon-wrapper mr-4">
            <v-icon size="40" color="white">mdi-tag-edit</v-icon>
          </div>
          <div class="flex-grow-1">
            <h2 class="text-h5 white--text font-weight-bold mb-1">
              {{ $t('oferta.editing') }}
            </h2>
            <p class="mb-0 white--text text-caption opacity-90">
              Edita los detalles de esta oferta
            </p>
          </div>
          <v-btn icon dark @click="cerrar" :disabled="loading">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- Mensaje de error -->
      <v-alert
        v-if="errorMessage"
        type="error"
        dismissible
        @input="errorMessage = ''"
        class="ma-4 mb-0"
        prominent
        border="left"
      >
        <v-row align="center">
          <v-col class="grow">
            {{ errorMessage }}
          </v-col>
        </v-row>
      </v-alert>

      <!-- Contenido del formulario -->
      <v-card-text class="pa-6" style="max-height: 600px;">
        <v-form ref="form" @submit.prevent="update">
          <!-- Sección: Información básica -->
          <div class="section-wrapper mb-6">
            <div class="section-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-information</v-icon>
              <span class="section-title">Información Básica</span>
            </div>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.nombre"
                  :label="$t('oferta.labels.nombre')"
                  outlined
                  dense
                  :error="hasInputError('nombre')"
                  :error-messages="getInputError('nombre')"
                  prepend-inner-icon="mdi-tag-text"
                  :rules="[v => !!v || 'Campo requerido']"
                  class="rounded-lg"
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.tipo"
                  :items="tiposOferta"
                  :label="$t('oferta.labels.tipo')"
                  outlined
                  dense
                  :error="hasInputError('tipo')"
                  :error-messages="getInputError('tipo')"
                  prepend-inner-icon="mdi-tag-multiple"
                  :rules="[v => !!v || 'Campo requerido']"
                  class="rounded-lg"
                  @change="onTipoChange"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-icon small :color="getTipoColor(item.value)" class="mr-2">
                          {{ getTipoIcon(item.value) }}
                        </v-icon>
                        {{ item.text }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>
              
              <v-col v-if="form.tipo === 'porcentaje'" cols="12" md="6">
                <v-text-field
                  v-model.number="form.valor"
                  type="number"
                  :label="$t('oferta.labels.valor')"
                  outlined
                  dense
                  suffix="%"
                  :error="hasInputError('valor')"
                  :error-messages="getInputError('valor')"
                  prepend-inner-icon="mdi-percent"
                  :rules="[
                    v => !!v || 'Campo requerido',
                    v => (v >= 0 && v <= 100) || 'Valor entre 0 y 100'
                  ]"
                  class="rounded-lg"
                />
              </v-col>

              <v-col v-if="form.tipo === 'por_cantidad'" cols="12" md="6">
                <v-text-field
                  v-model.number="form.cantidadMinima"
                  type="number"
                  min="1"
                  step="1"
                  :label="$t('oferta.labels.cantidadMinima')"
                  outlined
                  dense
                  :error="hasInputError('cantidadMinima')"
                  :error-messages="getInputError('cantidadMinima')"
                  prepend-inner-icon="mdi-numeric"
                  :rules="[
                    v => (v != null && v !== '' && Number.isInteger(Number(v)) && Number(v) >= 1) || 'Entero mayor o igual a 1'
                  ]"
                  class="rounded-lg"
                />
              </v-col>
              <v-col v-if="form.tipo === 'por_cantidad'" cols="12" md="6">
                <v-text-field
                  v-model.number="form.valor"
                  type="number"
                  :label="$t('oferta.labels.valor')"
                  outlined
                  dense
                  suffix="%"
                  :error="hasInputError('valor')"
                  :error-messages="getInputError('valor')"
                  prepend-inner-icon="mdi-percent"
                  :rules="[
                    v => !!v || 'Campo requerido',
                    v => (v >= 0 && v <= 100) || 'Valor entre 0 y 100'
                  ]"
                  class="rounded-lg"
                />
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="form.descripcion"
                  :label="$t('oferta.labels.descripcion')"
                  outlined
                  dense
                  rows="3"
                  :error="hasInputError('descripcion')"
                  :error-messages="getInputError('descripcion')"
                  prepend-inner-icon="mdi-text-box"
                  class="rounded-lg"
                />
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Sección: Productos y alcance -->
          <div class="section-wrapper mb-6">
            <div class="section-header mb-4">
              <v-icon color="success" class="mr-2">mdi-cart</v-icon>
              <span class="section-title">Productos y Alcance</span>
            </div>
            
            <oferta-form-fields
              ref="formFields"
              v-model="form"
              :input-errors="inputErrors"
            />
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Sección: Estado -->
          <div class="section-wrapper">
            <div class="section-header mb-3">
              <v-icon color="warning" class="mr-2">mdi-toggle-switch</v-icon>
              <span class="section-title">Estado de la Oferta</span>
            </div>
            
            <v-switch
              v-model="form.activa"
              :label="form.activa ? 'Oferta activa' : 'Oferta inactiva'"
              color="success"
              inset
              class="mt-2"
            >
              <template v-slot:label>
                <span :class="form.activa ? 'success--text font-weight-bold' : 'grey--text'">
                  {{ form.activa ? 'Oferta activa' : 'Oferta inactiva' }}
                </span>
              </template>
            </v-switch>
          </div>
        </v-form>
      </v-card-text>

      <!-- Footer con botones -->
      <v-divider></v-divider>
      <v-card-actions class="pa-4 bg-light">
        <v-spacer></v-spacer>
        
        <v-btn
          text
          large
          @click="cerrar"
          :disabled="loading"
          class="mr-2"
        >
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        
        <v-btn
          color="primary"
          large
          @click="update"
          :loading="loading"
          :disabled="loading"
          elevation="2"
          class="px-8"
        >
          <v-icon left>mdi-content-save</v-icon>
          Guardar Cambios
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import OfertaProvider from "../../../providers/OfertaProvider";
import ClientError from "@/errors/ClientError";
import OfertaFormFields from "../OfertaCreate/OfertaFormFields";

export default {
  name: "OfertaUpdate",
  components: { OfertaFormFields },
  props: {
    open: { type: Boolean, default: true },
    item: { type: Object, required: true },
  },
  data() {
    return {
      dialog: false,
      title: "oferta.editing",
      errorMessage: "",
      inputErrors: {},
      loading: false,
      form: {
        nombre: null,
        descripcion: null,
        tipo: null,
        valor: null,
        cantidadMinima: null,
        criterios: {
          productos: [],
          marcas: [],
          modelos: [],
          tamanios: [],
        },
        deposito: null,
        diasSemana: [],
        fechaInicio: null,
        fechaFin: null,
        mediosPago: [],
        activa: true,
      },
      tiposOferta: [
        { text: "Descuento Porcentual", value: "porcentaje" },
        { text: "Por cantidad", value: "por_cantidad" },
        { text: "2x1 - Lleva 2, Paga 1", value: "2x1" },
        { text: "3x2 - Lleva 3, Paga 2", value: "3x2" },
        { text: "4x3 - Lleva 4, Paga 3", value: "4x3" },
      ],
    };
  },
  watch: {
    open(val) {
      this.dialog = val;
    },
    dialog(val) {
      if (!val) {
        this.$emit('close');
      }
    },
    item: {
      handler(newVal) {
        if (newVal) {
          // Normalizar mediosPago para asegurar que coincidan con el enum del backend
          const mediosPagoNormalizados = (newVal.mediosPago || []).map(medio => {
            const mapeo = {
              'Tarjeta Débito': 'Tarjeta Debito',
              'Tarjeta Crédito': 'Tarjeta Credito',
            };
            return mapeo[medio] || medio;
          });
          
          this.form = {
            nombre: newVal.nombre || null,
            descripcion: newVal.descripcion || null,
            tipo: newVal.tipo || null,
            valor: newVal.valor != null ? newVal.valor : null,
            cantidadMinima: newVal.cantidadMinima != null ? newVal.cantidadMinima : null,
            criterios: {
              productos: newVal.criterios?.productos
                ? newVal.criterios.productos.map((p) => String((p && p.id) != null ? p.id : p))
                : [],
              marcas: newVal.criterios?.marcas ? newVal.criterios.marcas.map((m) => m.id || m) : [],
              modelos: newVal.criterios?.modelos ? newVal.criterios.modelos.map((m) => m.id || m) : [],
              tamanios: newVal.criterios?.tamanios ? newVal.criterios.tamanios.map((t) => t.id || t) : [],
            },
            deposito: newVal.deposito ? (newVal.deposito.id || newVal.deposito) : null,
            diasSemana: newVal.diasSemana || [],
            fechaInicio: newVal.fechaInicio || null,
            fechaFin: newVal.fechaFin || null,
            mediosPago: mediosPagoNormalizados,
            activa: newVal.activa !== undefined ? newVal.activa : true,
          };
        }
      },
      immediate: true,
    },
  },
  created() {
    this.dialog = this.open;
  },
  methods: {
    hasInputError(field) {
      return this.inputErrors && this.inputErrors[field];
    },
    getInputError(field) {
      if (this.inputErrors && this.inputErrors[field]) {
        return Array.isArray(this.inputErrors[field])
          ? this.inputErrors[field]
          : [this.inputErrors[field]];
      }
      return [];
    },
    getTipoIcon(tipo) {
      const icons = {
        porcentaje: 'mdi-percent',
        por_cantidad: 'mdi-cart-arrow-down',
        '2x1': 'mdi-numeric-2-box',
        '3x2': 'mdi-numeric-3-box',
        '4x3': 'mdi-numeric-4-box',
      };
      return icons[tipo] || 'mdi-tag';
    },
    getTipoColor(tipo) {
      const colors = {
        porcentaje: 'success',
        por_cantidad: 'teal',
        '2x1': 'primary',
        '3x2': 'info',
        '4x3': 'warning',
      };
      return colors[tipo] || 'grey';
    },
    onTipoChange() {
      if (this.form.tipo !== "porcentaje" && this.form.tipo !== "por_cantidad") {
        this.form.valor = null;
      }
      if (this.form.tipo !== "por_cantidad") {
        this.form.cantidadMinima = null;
      }
    },
    normalizeMediosPago(mediosPago) {
      const mapeo = {
        'Tarjeta Débito': 'Tarjeta Debito',
        'Tarjeta Crédito': 'Tarjeta Credito',
      };
      return (mediosPago || []).map(medio => mapeo[medio] || medio);
    },
    update() {
      if (this.$refs.form.validate() && this.$refs.formFields.validate()) {
        this.loading = true;
        const formData = {
          ...this.form,
          mediosPago: this.normalizeMediosPago(this.form.mediosPago),
          valor: this.form.valor !== null && this.form.valor !== undefined ? Number(this.form.valor) : null,
          cantidadMinima:
            this.form.cantidadMinima != null && this.form.cantidadMinima !== ""
              ? parseInt(this.form.cantidadMinima, 10)
              : null,
        };
        OfertaProvider.updateOferta({ id: this.item.id, ...formData })
          .then((r) => {
            if (r) {
              this.$emit("itemUpdated", r.data.ofertaUpdate);
              this.$emit("close");
            }
          })
          .catch((error) => {
            let clientError = new ClientError(error);
            this.inputErrors = clientError.inputErrors;
            this.errorMessage = clientError.i18nMessage;
          })
          .finally(() => (this.loading = false));
      }
    },
    cerrar() {
      this.dialog = false;
      setTimeout(() => {
        this.resetForm();
      }, 300);
    },
    resetForm() {
      this.errorMessage = "";
      this.inputErrors = {};
    },
  },
};
</script>

<style scoped>
.oferta-update-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.oferta-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
}

.oferta-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.opacity-90 {
  opacity: 0.9;
}

.section-wrapper {
  transition: all 0.3s ease;
}

.section-header {
  display: flex;
  align-items: center;
  padding-bottom: 8px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #424242;
}

.bg-light {
  background-color: #f5f5f5;
}

.rounded-lg >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
}

/* Animaciones */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-wrapper {
  animation: fadeInUp 0.4s ease-out;
}

/* Mejoras en los campos */
.v-text-field--outlined >>> fieldset,
.v-textarea--outlined >>> fieldset,
.v-select--outlined >>> fieldset {
  border-color: #e0e0e0;
  transition: all 0.3s ease;
}

.v-text-field--outlined:hover >>> fieldset,
.v-textarea--outlined:hover >>> fieldset,
.v-select--outlined:hover >>> fieldset {
  border-color: #667eea;
}

/* Botones con efectos hover */
.v-btn {
  transition: all 0.3s ease !important;
}

.v-btn:not(.v-btn--disabled):hover {
  transform: translateY(-2px);
}

.v-btn--text:hover {
  transform: translateY(0) !important;
}
</style>

